import type { ReactNode } from 'react'
import { Slot } from 'radix-ui'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Marks AI-created or AI-assisted data: a thin pale-green border, a faint green field and a small
 * green sparkle at the upper right (demo DESIGN.md). Use it on configuration and accessory data,
 * prices, calculations, lead times and comparisons; group related values instead of marking every
 * number. It is a passive annotation, never a control.
 */
export function AiData({
  children,
  label,
  className,
  asChild = false,
}: {
  children: ReactNode
  /** What the AI produced, for screen readers and the hover title: "AI lead-time estimate". */
  label: string
  className?: string
  asChild?: boolean
}) {
  const Component = asChild ? Slot.Root : 'div'
  return (
    <Component
      className={cn('relative min-w-0 rounded-md border border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/30', className)}
      data-ai-data={label}
    >
      <Slot.Slottable>{children}</Slot.Slottable>
      <span
        aria-label={label}
        className="pointer-events-none absolute -top-1.5 right-1.5 rounded-sm bg-emerald-50 p-0.5 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
        role="img"
        title={label}
      >
        <Sparkles aria-hidden="true" className="size-3" />
      </span>
    </Component>
  )
}
